/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export type ProductTourSide = 'top' | 'right' | 'bottom' | 'left';
export type ProductTourAlignment = 'start' | 'center' | 'end';
export type ProductTourStateStatus = 'completed' | 'dismissed';
export type ProductTourRevealSurface = 'profile-menu' | 'profile-language-menu';

export interface ProductTourStep {
  target: string;
  title: string;
  description: string;
  side?: ProductTourSide;
  align?: ProductTourAlignment;
  requiredPermissions?: string[];
  reveal?: ProductTourRevealSurface;
}

export interface ProductTourDefinition {
  id: string;
  applicationCode: string;
  version: number;
  priority?: number;
  autoStart?: boolean;
  steps: ProductTourStep[];
}

export interface ProductTourAccountState {
  schemaVersion: 1;
  tourVersion: number;
  status: ProductTourStateStatus;
  autoStartDisabled: boolean;
}

const TOUR_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
const APP_CODE_PATTERN = /^[A-Z][A-Z0-9]{1,15}$/;
const PERMISSION_PATTERN = /^[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/;
const SIDES = new Set<ProductTourSide>(['top', 'right', 'bottom', 'left']);
const ALIGNMENTS = new Set<ProductTourAlignment>(['start', 'center', 'end']);
const REVEAL_SURFACES = new Set<ProductTourRevealSurface>([
  'profile-menu',
  'profile-language-menu',
]);
const MAX_STEPS = 12;
const MAX_TITLE_LENGTH = 120;
const MAX_DESCRIPTION_LENGTH = 420;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function isProductTourApplicationCode(value: unknown): boolean {
  return typeof value === 'string' && APP_CODE_PATTERN.test(value.trim().toUpperCase());
}

function normalizeRequiredPermissions(value: unknown): string[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const permissions = value
    .filter((permission): permission is string => typeof permission === 'string')
    .map((permission) => permission.trim())
    .filter((permission) => PERMISSION_PATTERN.test(permission));
  if (permissions.length !== value.length) return undefined;
  return [...new Set(permissions)];
}

export function parseProductTourDefinition(value: unknown): ProductTourDefinition | null {
  if (!isRecord(value)) return null;

  const id = typeof value.id === 'string' ? value.id.trim() : '';
  const applicationCode = typeof value.applicationCode === 'string'
    ? value.applicationCode.trim().toUpperCase()
    : '';
  const version = Number(value.version);
  const priority = value.priority === undefined ? 0 : Number(value.priority);
  if (
    !TOUR_ID_PATTERN.test(id)
    || !APP_CODE_PATTERN.test(applicationCode)
    || !Number.isSafeInteger(version)
    || version < 1
    || !Number.isSafeInteger(priority)
    || priority < -1000
    || priority > 1000
    || !Array.isArray(value.steps)
    || value.steps.length === 0
    || value.steps.length > MAX_STEPS
  ) {
    return null;
  }

  const steps: ProductTourStep[] = [];
  for (const rawStep of value.steps) {
    if (!isRecord(rawStep)) return null;
    const target = typeof rawStep.target === 'string' ? rawStep.target.trim() : '';
    const title = typeof rawStep.title === 'string' ? rawStep.title.trim() : '';
    const description = typeof rawStep.description === 'string' ? rawStep.description.trim() : '';
    const side = rawStep.side as ProductTourSide | undefined;
    const align = rawStep.align as ProductTourAlignment | undefined;
    const reveal = rawStep.reveal as ProductTourRevealSurface | undefined;
    const requiredPermissions = normalizeRequiredPermissions(rawStep.requiredPermissions);
    if (
      !TOUR_ID_PATTERN.test(target)
      || !title
      || title.length > MAX_TITLE_LENGTH
      || !description
      || description.length > MAX_DESCRIPTION_LENGTH
      || (side !== undefined && !SIDES.has(side))
      || (align !== undefined && !ALIGNMENTS.has(align))
      || (reveal !== undefined && !REVEAL_SURFACES.has(reveal))
      || (rawStep.requiredPermissions !== undefined && requiredPermissions === undefined)
    ) {
      return null;
    }
    steps.push({
      target,
      title,
      description,
      ...(side ? { side } : {}),
      ...(align ? { align } : {}),
      ...(requiredPermissions ? { requiredPermissions } : {}),
      ...(reveal ? { reveal } : {}),
    });
  }

  return {
    id,
    applicationCode,
    version,
    priority,
    autoStart: value.autoStart !== false,
    steps,
  };
}

export function isProductTourRevealSurface(value: unknown): value is ProductTourRevealSurface {
  return typeof value === 'string' && REVEAL_SURFACES.has(value as ProductTourRevealSurface);
}

export function parseProductTourAccountState(value: unknown): ProductTourAccountState | null {
  if (!isRecord(value)) return null;
  const tourVersion = Number(value.tourVersion);
  if (
    value.schemaVersion !== 1
    || !Number.isSafeInteger(tourVersion)
    || tourVersion < 1
    || (value.status !== 'completed' && value.status !== 'dismissed')
    || typeof value.autoStartDisabled !== 'boolean'
  ) {
    return null;
  }
  return {
    schemaVersion: 1,
    tourVersion,
    status: value.status,
    autoStartDisabled: value.autoStartDisabled,
  };
}

export function productTourRegistryKey(definition: Pick<ProductTourDefinition, 'applicationCode' | 'id'>): string {
  return `${definition.applicationCode.toUpperCase()}:${definition.id}`;
}

export function productTourSettingKey(definition: Pick<ProductTourDefinition, 'applicationCode' | 'id'>): string {
  return `productTour.${definition.applicationCode.toUpperCase()}.${definition.id}`;
}

export function shouldAutoStartProductTour(
  definition: ProductTourDefinition,
  state: ProductTourAccountState | null,
): boolean {
  if (definition.autoStart === false || state?.autoStartDisabled) return false;
  return !state || state.tourVersion < definition.version;
}

export function isProductTourStepAuthorized(
  step: ProductTourStep,
  permissions: string[] | undefined,
  isAdmin = false,
): boolean {
  if (!step.requiredPermissions?.length) return true;
  if (isAdmin || permissions?.includes('*')) return true;
  const permissionSet = new Set(permissions ?? []);
  return step.requiredPermissions.every((permission) => permissionSet.has(permission));
}

export function escapeProductTourText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function productTourTargetSelector(target: string): string {
  if (!TOUR_ID_PATTERN.test(target)) return '';
  return `[data-tour-id="${target}"]`;
}

/**
 * Driver.js yatay hizalamayı fiziksel left/right olarak yorumlar. Üst/alt
 * popoverlarda logical start/end beklentisini RTL için Core adaptörü aynalar;
 * sol/sağ popoverlarda start/end dikey eksendir ve değişmez.
 */
export function resolveProductTourAlignmentForDirection(
  side: ProductTourSide | undefined,
  align: ProductTourAlignment | undefined,
  direction: string,
): ProductTourAlignment | undefined {
  const effectiveSide = side ?? 'bottom';
  const effectiveAlign = align ?? 'start';
  if (
    direction !== 'rtl'
    || (effectiveSide !== 'top' && effectiveSide !== 'bottom')
    || effectiveAlign === 'center'
  ) {
    return align;
  }
  return effectiveAlign === 'start' ? 'end' : 'start';
}

export function isVisibleProductTourElement(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}
